import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  companyId: string | null;
  companyName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CompanyDetails {
  id: string;
  name: string;
  status: string | null;
  email: string | null;
  website: string | null;
  company_phone_number: string | null;
  is_vat_registered: boolean | null;
  vat_number: string | null;
  vat_rate: number | null;
  street_address: string | null;
  suburb: string | null;
  city: string | null;
  province_state: string | null;
  postal_code: string | null;
  bank_name: string | null;
  branch_name: string | null;
  branch_code: string | null;
  account_number: string | null;
  account_holder: string | null;
  use_bank_info: boolean;
  suspension_type: string | null;
  suspension_reason: string | null;
  suspended_at: string | null;
  created_at: string;
  member_count: number;
  admin_count: number;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const StatusBadge = ({ status }: { status: string | null }) => {
  const s = (status ?? "").toLowerCase();
  if (s === "active") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Active
      </span>
    );
  }
  if (s === "suspended") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
        Suspended
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300 border border-slate-600">
      {status ?? "Unknown"}
    </span>
  );
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
    {children}
  </h3>
);

const DetailRow = ({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) => (
  <>
    <dt className="text-slate-500">{label}</dt>
    <dd className={`text-slate-200 ${className ?? ""}`}>
      {value === null || value === undefined || value === "" ? "—" : value}
    </dd>
  </>
);

export default function CompanyDetailSheet({ companyId, companyName, open, onOpenChange }: Props) {
  const [details, setDetails] = useState<CompanyDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setDetails(null);
      return;
    }
    setLoading(true);
    setError(null);

    Promise.all([
      supabase
        .from("companies" as any)
        .select(
          `id, name, status, email, website, company_phone_number,
           is_vat_registered, vat_number, "VAT Rate",
           street_address, suburb, city, province_state, postal_code,
           "Bank Name", "Branch Name", "Branch Code", "Account Number", "Account Holder", "Use Bank Info",
           suspension_type, suspension_reason, suspended_at, created_at`
        )
        .eq("id", companyId)
        .single(),
      supabase
        .from("company_members")
        .select("role, status")
        .eq("company_id", companyId)
        .eq("status", "active"),
    ]).then(([companyRes, membersRes]: [any, any]) => {
      if (companyRes.error) {
        setError(companyRes.error.message);
        setLoading(false);
        return;
      }
      const c = companyRes.data as any;
      const members = (membersRes.data ?? []) as Array<{ role: string | null; status: string | null }>;
      const member_count = members.length;
      const admin_count = members.filter((m) => m.role === "admin").length;
      setDetails({
        id: c.id,
        name: c.name,
        status: c.status,
        email: c.email,
        website: c.website,
        company_phone_number: c.company_phone_number,
        is_vat_registered: c.is_vat_registered,
        vat_number: c.vat_number,
        vat_rate: c["VAT Rate"],
        street_address: c.street_address,
        suburb: c.suburb,
        city: c.city,
        province_state: c.province_state,
        postal_code: c.postal_code,
        bank_name: c["Bank Name"],
        branch_name: c["Branch Name"],
        branch_code: c["Branch Code"],
        account_number: c["Account Number"],
        account_holder: c["Account Holder"],
        use_bank_info: c["Use Bank Info"] ?? false,
        suspension_type: c.suspension_type,
        suspension_reason: c.suspension_reason,
        suspended_at: c.suspended_at,
        created_at: c.created_at,
        member_count,
        admin_count,
      });
      setLoading(false);
    });
  }, [companyId]);

  const hasAddress =
    details &&
    (details.street_address ||
      details.suburb ||
      details.city ||
      details.province_state ||
      details.postal_code);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-lg w-full overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="text-white">{details?.name ?? companyName ?? "Company"}</SheetTitle>
          <SheetDescription asChild>
            <div>
              <StatusBadge status={details?.status ?? null} />
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {error ? (
            <p className="text-sm text-red-400">{error}</p>
          ) : loading || !details ? (
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-5 w-full bg-slate-800" />
              ))}
            </div>
          ) : (
            <>
              <section>
                <SectionHeading>Overview</SectionHeading>
                <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
                  <DetailRow label="Status" value={<StatusBadge status={details.status} />} />
                  <DetailRow label="Members" value={details.member_count} />
                  <DetailRow
                    label="Admins"
                    value={details.admin_count}
                    className={details.admin_count === 0 ? "text-red-400 font-semibold" : ""}
                  />
                  <DetailRow label="Email" value={details.email} />
                  <DetailRow label="Phone" value={details.company_phone_number} />
                  <DetailRow
                    label="Website"
                    value={
                      details.website ? (
                        <a
                          href={details.website.startsWith("http") ? details.website : `https://${details.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-400 hover:underline"
                        >
                          {details.website}
                        </a>
                      ) : null
                    }
                  />
                  <DetailRow label="VAT registered" value={details.is_vat_registered ? "Yes" : "No"} />
                  {details.is_vat_registered && (
                    <>
                      <DetailRow label="VAT number" value={details.vat_number} />
                      <DetailRow
                        label="VAT rate"
                        value={details.vat_rate !== null && details.vat_rate !== undefined ? `${details.vat_rate}%` : null}
                      />
                    </>
                  )}
                  <DetailRow label="Created" value={formatDate(details.created_at)} />
                </dl>
              </section>

              {hasAddress && (
                <section>
                  <SectionHeading>Address</SectionHeading>
                  <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
                    <DetailRow label="Street" value={details.street_address} />
                    <DetailRow label="Suburb" value={details.suburb} />
                    <DetailRow label="City" value={details.city} />
                    <DetailRow label="Province" value={details.province_state} />
                    <DetailRow label="Postal code" value={details.postal_code} />
                  </dl>
                </section>
              )}

              {details.use_bank_info && (
                <section>
                  <SectionHeading>Banking</SectionHeading>
                  <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
                    <DetailRow label="Bank" value={details.bank_name} />
                    <DetailRow label="Branch" value={details.branch_name} />
                    <DetailRow label="Branch code" value={details.branch_code} />
                    <DetailRow label="Account number" value={details.account_number} />
                    <DetailRow label="Account holder" value={details.account_holder} />
                  </dl>
                </section>
              )}

              {details.status === "suspended" && (
                <section>
                  <SectionHeading>Suspension</SectionHeading>
                  <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
                    <DetailRow label="Type" value={details.suspension_type} />
                    <DetailRow label="Reason" value={details.suspension_reason} />
                    <DetailRow label="Suspended at" value={formatDate(details.suspended_at)} />
                  </dl>
                </section>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
